import $ from 'jquery';
import PropTypes from 'prop-types';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import * as browser from 'lib/Browser';
import wyciwyp from 'notebook/mediocre/clipboard/wyciwyp';
import {Format, FormatColor, FormatData} from 'notebook/mediocre/formatted-text/FormattersConstants';
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import {COLORS} from 'ui-library/ColorPickerConstants';
import * as logging from 'util/Logging';

import formatters from './formatters';

let HAS_LOGGED = false;

// Maintain maps for formats from the client representation to DOM representation
type SupportedFormats = {
  [
    Property in keyof Pick<
      Format,
      'bold' | 'italic' | 'underline' | 'strikethrough' | 'subscript' | 'superscript'
    >
  ]: string;
};

const SUPPORTED_FORMATS: SupportedFormats = {
  bold: 'bold',
  italic: 'italic',
  underline: 'underline',
  strikethrough: 'strikethrough',
  subscript: 'subscript',
  superscript: 'superscript',
};

const FORMATS_FROM_RANGE: Array<keyof Format> = ['monospace'];

type SupportedFormatValues = {
  [Property in keyof Pick<Format, 'foregroundColor' | 'backgroundColor'>]: {
    domStyle: string;
    parseDOMValue: (value: string) => string | null;
    extractValueFromData: (data?: FormatColor['data']) => string;
  };
};

function extractColorValueFromData(data?: FormatColor['data']): string {
  if (data == null || data.color === COLORS.NO_STYLE) {
    return 'inherit';
  } else {
    return data.color;
  }
}

const SUPPORTED_FORMAT_VALUES: SupportedFormatValues = {
  foregroundColor: {
    domStyle: 'foreColor',
    parseDOMValue: wyciwyp.parseDOMColor,
    extractValueFromData: extractColorValueFromData,
  },
  backgroundColor: {
    domStyle: 'backColor',
    parseDOMValue(domValue: string): string | null {
      const color = wyciwyp.parseDOMColor(domValue);
      // The browser seems to default to white if there are multiple background colors in the selection.
      if (color === COLORS.WHITE) {
        return null;
      }
      return color;
    },
    extractValueFromData: extractColorValueFromData,
  },
};

// @ts-expect-error
const FormatState: {
  _getFormatsFromRange: () => Format;
  __testing__reset: () => void;
  supports: (format: string) => boolean;
  toggleFormat: (format: string, data?: FormatData) => void;
  readFormats: () => {[format: string]: boolean | string | null};
  defaultFormats: () => {[format: string]: boolean | null};
  propType: PropTypes.Requireable<PropTypes.InferProps<{}>>;
} = {};

FormatState._getFormatsFromRange = () => {
  const range = selutil.getRange();
  if (!range) {
    return null;
  }
  const $div = $(selutil.getEditable(range)!);
  if ($div.length === 0) {
    return null;
  }
  const formats = $div.data('formats');
  if (!formats) {
    return null;
  }
  return formats;
};

FormatState.__testing__reset = function (): void {
  HAS_LOGGED = false;
};

FormatState.supports = (format: string) => format in SUPPORTED_FORMATS || format in SUPPORTED_FORMAT_VALUES;

FormatState.toggleFormat = function (format: string, data?: {color: string}): void {
  if (format in SUPPORTED_FORMATS) {
    document.execCommand(SUPPORTED_FORMATS[format as keyof SupportedFormats]!);
  } else if (
    assertAndContinue(format in SUPPORTED_FORMAT_VALUES, `toggleFormat: unexpected format '${format}'`)
  ) {
    const {domStyle, extractValueFromData} = SUPPORTED_FORMAT_VALUES[format as keyof SupportedFormatValues];
    document.execCommand(domStyle, false, extractValueFromData(data));
  }
};

/**
 * Returns the result of `fn`, returning fallbackValue if executed in a non-browser context or if
 * a NS_ERROR_FAILURE error occurs (when calling queryCommandState/queryCommandValue)
 */
function _queryCommandWrapper<T>(fn: () => T, fallbackValue: T): T {
  if (browser.isBrowser) {
    try {
      return fn();
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (error) {
      // Explicitly catch error when calling `queryCommandState`.
      // NS_ERROR_FAILURE happens in Firefox on page leave, see: https://github.com/guardian/scribe/issues/435
      if (!HAS_LOGGED && error.name !== 'NS_ERROR_FAILURE') {
        HAS_LOGGED = true;
        logging.error(error, {extra: {message: 'Silently ignoring queryCommand future errors for session'}});
      }
    }
  }
  return fallbackValue;
}

FormatState.readFormats = function (): {[format: string]: boolean | string | null} {
  const rv: {[format: string]: boolean | string | null} = {};
  for (const format of Object.keys(SUPPORTED_FORMATS)) {
    const domStyle = SUPPORTED_FORMATS[format as keyof SupportedFormats]!;
    rv[format] = _queryCommandWrapper(() => document.queryCommandState(domStyle), false);
  }
  for (const format of Object.keys(SUPPORTED_FORMAT_VALUES)) {
    const {domStyle, parseDOMValue} = SUPPORTED_FORMAT_VALUES[format as keyof SupportedFormatValues];
    rv[format] = _queryCommandWrapper(() => parseDOMValue(document.queryCommandValue(domStyle)), null);
  }

  const sel = selutil.getSelectionIndices();
  const formats = FormatState._getFormatsFromRange();
  for (const format of FORMATS_FROM_RANGE) {
    rv[format] = formats ? formatters.isSelectionFormatted(sel, formats, format) : false;
  }
  return rv;
};

const getDefaultFormats = function (): {[format: string]: boolean | null} {
  const rv: {[format: string]: boolean | null} = {};
  for (const format of Object.keys(SUPPORTED_FORMATS)) {
    rv[format] = false;
  }
  for (const format of Object.keys(SUPPORTED_FORMAT_VALUES)) {
    rv[format] = null;
  }
  for (const format of FORMATS_FROM_RANGE) {
    rv[format] = false;
  }
  return rv;
};
FormatState.defaultFormats = getDefaultFormats;

const formatPropMap = Object.keys(SUPPORTED_FORMATS).map((format) => [format, PropTypes.bool.isRequired]);
const formatValuesPropsMap = Object.keys(SUPPORTED_FORMAT_VALUES).map((format) => [format, PropTypes.string]);
// eslint-disable-next-line no-restricted-syntax
FormatState.propType = PropTypes.shape(_.object(_.extend(formatPropMap, formatValuesPropsMap)));

export default FormatState;
